import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';

import { useAppDispatch } from '../../../stores/redux.store';
import { resetLoginForm } from '../../../actions/login-actions';
import Bull from '../../../components/BulletSeparator';
import ButtonLink from '../../../components/ButtonLink';
import { OidcContext, OidcDisplay, URLParameters } from '../../../types';
import { isOidcDisplayMode } from '../../../utilities/oidc/oidc';
import { getCurrentCloudType, isResetPasswordEnabled } from '../../../utilities/env';

import { LoginPageMessages } from '../messages';
import { loginPageId } from '../constants';
import { ResetPasswordLink } from './ResetPasswordLink';
import { LinkToSignupPage } from './LinkToSignup';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';

const WhiteboxFooter = ({
  isMobileApp,
  oidcContext,
  showSocialLoginOnly,
}: {
  isMobileApp: boolean;
  oidcContext?: OidcContext;
  showSocialLoginOnly: boolean | null;
}) => {
  const dispatch = useAppDispatch();

  const analyticsClient = useAnalyticsClient();

  const isCommercial = getCurrentCloudType() === 'commercial';

  const queryParams: URLParameters = queryString.parse(location.search);

  const signupLinkEnabled = useMemo(() => {
    // Due to Apple Store rules, we don't want sign up link on login page in Mobile OAuth case, unless we provide redirection from login to signup
    const shouldRenderSignupLink =
      isCommercial &&
      (!isMobileApp || isOidcDisplayMode(oidcContext, OidcDisplay.LoginWithRedirect));

    return queryParams.shide !== 'true' && shouldRenderSignupLink;
  }, [isCommercial, isMobileApp, oidcContext, queryParams.shide]);

  if (showSocialLoginOnly) {
    return (
      <ButtonLink
        id="use-a-different-login-method"
        spacing="none"
        onClick={() => {
          dispatch(resetLoginForm());
        }}>
        <FormattedMessage {...LoginPageMessages.socialFormUseDifferentLoginMethod} />
      </ButtonLink>
    );
  }

  const resetPasswordLinkEnabled = isResetPasswordEnabled();

  return (
    <ul>
      {resetPasswordLinkEnabled && (
        <li>
          <ResetPasswordLink
            onClick={() => analyticsClient.linkClickedEvent(loginPageId, 'cantLoginLink')}>
            <FormattedMessage id="trouble.logging.in" defaultMessage="Can't log in?" />
          </ResetPasswordLink>
        </li>
      )}
      {signupLinkEnabled && (
        <>
          {resetPasswordLinkEnabled && <Bull />}
          <li>
            <LinkToSignupPage
              onClick={() => analyticsClient.linkClickedEvent(loginPageId, 'signUpLink')}>
              <FormattedMessage {...LoginPageMessages.createAnAccount} />
            </LinkToSignupPage>
          </li>
        </>
      )}
    </ul>
  );
};

export default WhiteboxFooter;
